import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * A standalone rate limit component for public pages
 * Shows a countdown timer and message when rate limit is reached
 */
export function PublicRateLimitState() {
    const RATE_LIMIT_DURATION = 60; // seconds
    const STORAGE_KEY = 'publicRateLimitStartTime';

    // Initialize time left based on stored start time or create new start time
    const [timeLeft, setTimeLeft] = useState(() => {
        const storedStartTime = sessionStorage.getItem(STORAGE_KEY);
        if (storedStartTime) {
            const elapsedTime = Math.floor((Date.now() - parseInt(storedStartTime)) / 1000);
            // If more than RATE_LIMIT_DURATION seconds have passed, clear it
            if (elapsedTime >= RATE_LIMIT_DURATION) {
                sessionStorage.removeItem(STORAGE_KEY);
                return 0; // This will trigger a refresh and get a new time from the auth hook
            }
            return Math.max(0, RATE_LIMIT_DURATION - elapsedTime);
        }
        // Set a new start time if none exists
        sessionStorage.setItem(STORAGE_KEY, Date.now().toString());
        return RATE_LIMIT_DURATION;
    });

    // Update the timer every second
    useEffect(() => {
        if (timeLeft <= 0) {
            sessionStorage.removeItem(STORAGE_KEY);
            // Refresh the page to try again
            window.location.reload();
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft(prevTime => {
                if (prevTime <= 1) {
                    clearInterval(timer);
                    sessionStorage.removeItem(STORAGE_KEY);
                    // Refresh the page to try again
                    window.location.reload();
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    // Format time in MM:SS format
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="text-center space-y-6">
            {/* Icon with pulse animation */}
            <div className="mx-auto w-20 h-20 bg-blue-100 dark:bg-gray-700 rounded-full flex items-center justify-center 
                        animate-pulse mb-2 relative">
                <span className="text-4xl">⏳</span>
                {/* Circular progress */}
                <svg className="absolute inset-0 w-full h-full -rotate-90">
                    <circle
                        className="text-gray-200 dark:text-gray-600"
                        strokeWidth="4"
                        stroke="currentColor"
                        fill="transparent"
                        r="38"
                        cx="40"
                        cy="40"
                    />
                    <circle
                        className="text-blue-500 dark:text-blue-400"
                        strokeWidth="4"
                        strokeDasharray={`${2 * Math.PI * 38}`}
                        strokeDashoffset={`${2 * Math.PI * 38 * (1 - timeLeft / RATE_LIMIT_DURATION)}`}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r="38"
                        cx="40"
                        cy="40"
                    />
                </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Rate Limit Reached
            </h2>
            
            <p className="text-gray-600 dark:text-gray-300">
                You've reached the maximum number of requests allowed in a short period.
                Please wait <span className="font-semibold text-blue-600 dark:text-blue-400">{formatTime(timeLeft)}</span> or log in for unlimited access.
            </p>
            
            <div className="pt-4">
                <Link
                    to="/login"
                    className="mb-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    Log in for unlimited access
                </Link>
            </div>
        </div>
    );
}
